import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './LabApoio.css';
import { API_URL } from '../config';
import Pagination from '../components/Pagination';
import { situacaoExameLabel, situacaoExameMap } from '../utils/situacaoExame';
import { formatDate } from '../utils/formatDate';

const styles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
  btnPesquisar: {
    padding: '8px 20px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
};

function LabApoio() {
  const navigate = useNavigate();
  const [exames, setExames] = useState([]);
  const [allExames, setAllExames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [situacaoStats, setSituacaoStats] = useState([]);

  // Pré-preencher com últimos 30 dias
  const getDefaultDates = () => {
    const hoje = new Date();
    const dataFim = hoje.toISOString().split('T')[0];
    const dataInicio = new Date(hoje.setDate(hoje.getDate() - 30)).toISOString().split('T')[0];
    return { dataInicio, dataFim };
  };

  const [filtros, setFiltros] = useState({
    search: '',
    dataInicio: getDefaultDates().dataInicio,
    dataFim: getDefaultDates().dataFim,
    situacao: ''
  });

  // Fetch on mount + when page changes
  useEffect(() => {
    carregarExames();
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    carregarExames(1);
  };

  const carregarExames = async (page) => {
    const activePage = page || currentPage;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.search) params.append('search', filtros.search);
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      if (filtros.situacao) params.append('situacao', filtros.situacao);

      const response = await axios.get(`${API_URL}/lab-apoio?${params.toString()}`);
      const { data, situacaoStats } = response.data;

      setAllExames(data || []);
      setTotalItems((data || []).length);
      setSituacaoStats(situacaoStats || []);

      // Aplicar paginação
      const startIndex = (activePage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = (data || []).slice(startIndex, endIndex);
      setExames(paginatedData);
      setError('');
    } catch (err) {
      console.error('Erro ao carregar exames lab apoio:', err);
      setError('Erro ao carregar exames de laboratórios de apoio');
    } finally {
      setLoading(false);
    }
  };

  // formatDate importado de ../utils/formatDate

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    if (typeof timeString === 'string' && timeString.includes('T')) {
      const date = new Date(timeString);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (typeof timeString === 'string' && timeString.includes(':')) {
      const parts = timeString.split(':');
      return `${parts[0]}:${parts[1]}`;
    }
    return timeString;
  };

  const abrirPDF = async (exameId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/solicitacao-exames/${exameId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Erro ao abrir PDF:', error);
      alert('Erro ao carregar PDF');
    }
  };

  const limparFiltros = () => {
    const { dataInicio, dataFim } = getDefaultDates();
    setFiltros({
      search: '',
      dataInicio,
      dataFim,
      situacao: ''
    });
    setCurrentPage(1);
  };

  return (
    <div className="lab-apoio-container">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>🏥</span>
          <div>
            <h1 style={styles.pageTitle}>Laboratórios de Apoio</h1>
            <p style={styles.pageSubtitle}>Exames realizados por laboratórios parceiros</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtro-grupo">
          <label>Buscar (Protocolo/Paciente/Exame)</label>
          <input
            type="text"
            placeholder="Pesquisar..."
            value={filtros.search}
            onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </div>

        <div className="filtro-grupo">
          <label>Data Início</label>
          <input
            type="date"
            value={filtros.dataInicio}
            onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })}
          />
        </div>

        <div className="filtro-grupo">
          <label>Data Fim</label>
          <input
            type="date"
            value={filtros.dataFim}
            onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })}
          />
        </div>

        <div className="filtro-grupo">
          <label>Situação</label>
          <select
            value={filtros.situacao}
            onChange={(e) => setFiltros({ ...filtros, situacao: e.target.value })}
          >
            <option value="">Todas</option>
            {Object.entries(situacaoExameMap).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>

        <button onClick={handleSearch} style={styles.btnPesquisar}>
          Pesquisar
        </button>

        <button onClick={limparFiltros} className="btn-limpar-filtros">
          Limpar Filtros
        </button>
      </div>

      {/* Cards agrupados por situação */}
      {!loading && situacaoStats.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px', fontSize: '1.1rem', color: '#333' }}>Distribuição por Situação</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
            {situacaoStats.map((stat, index) => (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: `linear-gradient(135deg, ${
                    stat.SITUACAO === 'D' ? '#f59e0b 0%, #d97706 100%' :
                    stat.SITUACAO === 'C' ? '#3b82f6 0%, #2563eb 100%' :
                    stat.SITUACAO === 'L' ? '#10b981 0%, #059669 100%' :
                    index % 5 === 0 ? '#667eea 0%, #764ba2 100%' :
                    index % 5 === 1 ? '#3b82f6 0%, #2563eb 100%' :
                    index % 5 === 2 ? '#10b981 0%, #059669 100%' :
                    index % 5 === 3 ? '#f59e0b 0%, #d97706 100%' :
                    '#ef4444 0%, #dc2626 100%'
                  })`,
                  padding: '20px',
                  borderRadius: '10px',
                  color: 'white',
                  textAlign: 'center',
                  minWidth: '180px'
                }}
              >
                <h3 style={{ margin: '0 0 5px 0', fontSize: '2rem' }}>{stat.total}</h3>
                <p style={{ margin: 0, opacity: 0.9 }}>
                  {situacaoExameLabel(stat.SITUACAO)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estatísticas */}
      {!loading && (
        <div className="stats-info">
          <p>
            Total de exames encontrados: <strong>{totalItems}</strong>
            {exames.length > 0 && (
              <span style={{ marginLeft: '20px', color: '#6b7280' }}>
                Exibindo {exames.length} registros nesta página
              </span>
            )}
          </p>
        </div>
      )}

      {/* Tabela */}
      {loading ? (
        <div className="loading">Carregando exames...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="table-container">
          <table className="exames-table">
            <thead>
              <tr>
                <th>Protocolo</th>
                <th>Data</th>
                <th>Paciente</th>
                <th>Exame</th>
                <th>Descrição</th>
                <th>Data Coleta</th>
                <th>Situação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {exames.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    Nenhum exame de laboratório de apoio encontrado
                  </td>
                </tr>
              ) : (
                exames.map((exame) => (
                  <tr key={exame.EXAME_ID}>
                    <td>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/protocolo/${exame.PROTOCOLO}`);
                        }}
                        style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}
                      >
                        {exame.PROTOCOLO}
                      </a>
                    </td>
                    <td>{formatDate(exame.DATA)}</td>
                    <td>{exame.PACIENTE_NOME || '-'}</td>
                    <td>{exame.EXAME}</td>
                    <td>{exame.EXAME_DESCRICAO || '-'}</td>
                    <td>{formatDate(exame.DATACOLETA)}</td>
                    <td>
                      <span className={`situacao-badge situacao-${exame.SITUACAO}`}>
                        {situacaoExameLabel(exame.SITUACAO)}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => abrirPDF(exame.EXAME_ID)}
                        className="btn-ver-pdf"
                        title="Ver PDF do exame"
                      >
                        Ver PDF
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginação */}
      {!loading && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
        />
      )}
    </div>
  );
}

export default LabApoio;
